import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DepartmentCostPosition, CreateDepartmentCostPositionInput } from '@/types/database/department-cost-positions';
import { ExpenseCategory } from '@/types/database/expense-categories';
import { toast } from 'sonner';

// Fetch all department-cost position mappings
export const useDepartmentCostPositions = () => {
  return useQuery({
    queryKey: ['department-cost-positions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('department_cost_positions')
        .select(`
          *,
          department:departments(*),
          category:expense_categories(*)
        `)
        .order('created_at');
      
      if (error) throw error;
      return data as DepartmentCostPosition[];
    },
  });
};

// Fetch cost positions available for a specific department
// Returns: department-specific cost positions + global cost positions
export const useCostPositionsForDepartment = (departmentId: string | null) => {
  return useQuery({
    queryKey: ['cost-positions-for-department', departmentId],
    queryFn: async () => {
      if (!departmentId) {
        // If no department selected, return only global cost positions
        const { data, error } = await supabase
          .from('expense_categories')
          .select('*')
          .eq('is_recordable', true)
          .eq('is_active', true)
          .eq('is_global', true)
          .order('name');
        
        if (error) throw error;
        return data as ExpenseCategory[];
      }

      // Get department-specific cost positions
      const { data: deptPositions, error: deptError } = await supabase
        .from('department_cost_positions')
        .select('category:expense_categories(*)')
        .eq('department_id', departmentId);
      
      if (deptError) throw deptError;

      // Get global cost positions
      const { data: globalPositions, error: globalError } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('is_recordable', true)
        .eq('is_active', true)
        .eq('is_global', true);
      
      if (globalError) throw globalError;

      // Combine and deduplicate - cast the database types to our interface
      const deptCategories = deptPositions
        ?.map(dp => dp.category as ExpenseCategory | null)
        .filter((cat): cat is ExpenseCategory => 
          cat !== null && cat.is_active === true && cat.is_recordable === true
        ) || [];
      
      // Cast global positions to our type as well
      const globalCats = (globalPositions || []) as ExpenseCategory[];
      const allCategories = [...deptCategories, ...globalCats];
      
      // Remove duplicates by id
      const uniqueCategories = allCategories.reduce((acc, cat) => {
        if (!acc.find(c => c.id === cat.id)) {
          acc.push(cat);
        }
        return acc;
      }, [] as ExpenseCategory[]);
      
      return uniqueCategories.sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: true, // Always enabled, will return global only if no department
  });
};

// Assign a cost position to a department
export const useAssignCostPositionToDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: CreateDepartmentCostPositionInput) => {
      const { data, error } = await supabase
        .from('department_cost_positions')
        .insert(input)
        .select()
        .single();
      
      if (error) throw error;
      return data as DepartmentCostPosition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-cost-positions'] });
      queryClient.invalidateQueries({ queryKey: ['cost-positions-for-department'] });
      toast.success('Cost position assigned to department');
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast.error('This cost position is already assigned to this department');
      } else {
        toast.error(`Failed to assign cost position: ${error.message}`);
      }
    },
  });
};

// Remove a cost position from a department
export const useRemoveCostPositionFromDepartment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('department_cost_positions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-cost-positions'] });
      queryClient.invalidateQueries({ queryKey: ['cost-positions-for-department'] });
      toast.success('Cost position removed from department');
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove cost position: ${error.message}`);
    },
  });
};

// Toggle global status for a category
export const useToggleCategoryGlobal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, is_global }: { id: string; is_global: boolean }) => {
      const { data, error } = await supabase
        .from('expense_categories')
        .update({ is_global })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as ExpenseCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      queryClient.invalidateQueries({ queryKey: ['cost-positions-for-department'] });
      toast.success('Category updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update category: ${error.message}`);
    },
  });
};
